import { useCallback, useMemo } from 'react';
import type {
  CustomChartDefinition,
  CustomChartTableSettings,
  DashboardPreset,
  DashboardPresetContent,
  DashboardPresetFilters,
} from '@/entities/dashboards/model/api/types';
import {
  toSharedPresetListFields,
  useReadCachedSharedPreset,
  useSharedPresets,
  useSharePreset,
  useUpdateSharedPreset,
  type SharedPreset,
  type SharedPresetTransfer,
  type SharedPresetUser,
} from '@/entities/shared-presets';
import { isQueryDataReceived } from '@/shared/api';
import {
  buildSharedDashboardPresetId,
  DASHBOARD_PRESETS_TABLE_NAME,
  parseSharedDashboardPresetId,
} from './dashboardPresetKeys';
import { useServerPresetMutations } from './useServerPresetMutations';
import { useSettings } from './useSettings';
import { isDashboardPresetFilters } from './utils/normalizeUiSettings';

/** Дашборд в списке страницы дашбордов: личный либо совместный, с правами на него. */
export type DashboardPresetItem = DashboardPreset & {
  /** Дашборд совместный — им владелец поделился с выбранными пользователями */
  isShared: boolean;
  /** Владелец совместного дашборда */
  owner?: SharedPresetUser;
  /** Получатели совместного дашборда — известны только его владельцу */
  recipients?: SharedPresetUser[];
  /** Текущий пользователь вправе править дашборд: свой личный либо совместный, которым владеет */
  isEditable: boolean;
};

const byName = (a: DashboardPresetItem, b: DashboardPresetItem): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Хук работы с пользовательскими дашбордами-пресетами страницы дашбордов.
 *
 * Пресет здесь — самостоятельный состав диаграмм, а не снимок текущего вида экрана: создание
 * заводит новый дашборд, а правка меняет его состав. Поэтому «сохранить текущее состояние в
 * пресет» — операции, обратной применению, — у дашбордов нет.
 *
 * Личные дашборды лежат в настройках пользователя, совместные — на сервере и попадают в тот же
 * список перед личными. У совместного личные применение и закрепление, а название, состав и фильтры
 * панели общие для владельца и получателей. Пока совместные не получены, отсутствие дашборда в списке
 * ещё ничего не означает (`isPresetListKnown`).
 *
 * Фильтры панели получатель вправе сменить у себя: смена запоминается за ним и действует, пока он её
 * не сбросит, — правка фильтров владельцем до него тем временем не доходит.
 */
export const useDashboardPresets = () => {
  const {
    userId,
    dashboardPresets,
    createDashboardPreset,
    updateDashboardPreset,
    applyDashboardPreset,
    clearActiveDashboardPreset,
    deleteDashboardPreset,
    setDashboardPresetPinned,
    setDashboardChartTableSettings,
    setDashboardSharedFilters,
    pruneDashboardSharedFilters,
  } = useSettings();

  const sharedPresetsQuery = useSharedPresets<DashboardPresetContent>(DASHBOARD_PRESETS_TABLE_NAME);
  const sharedPresetsData = sharedPresetsQuery.data;
  const { mutateAsync: updateSharedPreset, isPending: isUpdatingSharedPreset } =
    useUpdateSharedPreset<DashboardPresetContent>();
  // Запись фильтров владельцем — своим экземпляром: смена фильтров не должна выглядеть
  // записью состава из режима редактирования
  const { mutateAsync: updateSharedFilters } = useUpdateSharedPreset<DashboardPresetContent>();
  const readCachedSharedPreset = useReadCachedSharedPreset<DashboardPresetContent>(DASHBOARD_PRESETS_TABLE_NAME);
  const { pinServerPreset, deleteServerPreset } = useServerPresetMutations({
    parseSharedId: parseSharedDashboardPresetId,
  });

  const sharedPresets = useMemo<DashboardPresetItem[]>(() => {
    const list = (sharedPresetsData?.items ?? []).map<DashboardPresetItem>((preset) => ({
      id: buildSharedDashboardPresetId(preset.id),
      ...toSharedPresetListFields(preset),
      charts: preset.settings.charts,
      // Содержимое совместного сервер хранит как есть: непонятные фильтры показываются умолчанием,
      // а не роняют панель
      filters: isDashboardPresetFilters(preset.settings.filters) ? preset.settings.filters : undefined,
    }));
    list.sort(byName);
    return list;
  }, [sharedPresetsData]);

  const personalPresets = useMemo<DashboardPresetItem[]>(() => {
    const list = Object.values(dashboardPresets.presets ?? {}).map<DashboardPresetItem>((preset) => ({
      ...preset,
      isShared: false,
      isEditable: true,
    }));
    list.sort(byName);
    return list;
  }, [dashboardPresets.presets]);

  const presets = useMemo(() => [...sharedPresets, ...personalPresets], [sharedPresets, personalPresets]);

  // Отказ запроса совместных — не пустой список: до ответа отсутствие дашборда ничего не означает
  const isPresetListKnown = isQueryDataReceived(sharedPresetsQuery);

  const activePresetId = dashboardPresets.activePresetId;
  const activePreset = useMemo(
    () => presets.find((preset) => preset.id === activePresetId) ?? null,
    [presets, activePresetId],
  );

  const sharedFilters = dashboardPresets.sharedFilters;

  /** Фильтры дашборда для текущего пользователя: у получателя — свои, если он их менял. */
  const resolveFilters = useCallback(
    (preset: DashboardPresetItem): DashboardPresetFilters | null => {
      const ownFilters = preset.isShared && !preset.isEditable ? sharedFilters?.[preset.id] : undefined;
      return ownFilters ?? preset.filters ?? null;
    },
    [sharedFilters],
  );

  const activeFilters = useMemo(
    () => (activePreset ? resolveFilters(activePreset) : null),
    [activePreset, resolveFilters],
  );

  const createPreset = useCallback(
    (name: string, charts: CustomChartDefinition[], filters: DashboardPresetFilters): string =>
      createDashboardPreset(userId, name, charts, filters),
    [createDashboardPreset, userId],
  );

  /**
   * Собирает содержимое совместного дашборда для записи — всегда обоими полями ({@link DashboardPresetContent}).
   * Незатронутая половина — состав или фильтры — берётся из последнего полученного списка в момент
   * записи: отложенная запись фильтров не должна вернуть состав, записанный после её отложения.
   */
  const buildSharedContent = useCallback(
    (
      sharedId: number,
      changes: { charts?: CustomChartDefinition[]; filters?: DashboardPresetFilters },
    ): DashboardPresetContent | undefined => {
      if (!changes.charts && !changes.filters) return undefined;
      const stored = readCachedSharedPreset(sharedId)?.settings;
      const charts = changes.charts ?? stored?.charts;
      // Без состава записывать нечего: пустой состав стёр бы диаграммы дашборда у всех
      if (!charts) return undefined;
      const storedFilters = isDashboardPresetFilters(stored?.filters) ? stored.filters : undefined;
      return { charts, filters: changes.filters ?? storedFilters };
    },
    [readCachedSharedPreset],
  );

  /**
   * Записывает название, состав и/или фильтры. Совместный дашборд записывается на сервере, поэтому
   * итог приходит обещанием: `false` — сервер правку не принял, о причине сообщил общий перехватчик.
   */
  const updatePreset = useCallback(
    async (
      presetId: string,
      changes: { name?: string; charts?: CustomChartDefinition[]; filters?: DashboardPresetFilters },
    ): Promise<boolean> => {
      const sharedId = parseSharedDashboardPresetId(presetId);
      if (sharedId === null) {
        updateDashboardPreset(userId, presetId, changes);
        return true;
      }
      const settings = buildSharedContent(sharedId, changes);
      const updated = await updateSharedPreset({ id: sharedId, name: changes.name, settings }).catch(() => null);
      return updated !== null;
    },
    [updateDashboardPreset, updateSharedPreset, buildSharedContent, userId],
  );

  /**
   * Запоминает смену фильтров применённого дашборда там, откуда они взяты: у личного — в нём самом,
   * у совместного владельца — на сервере, у получателя — за ним самим. `false` — сервер смену не
   * принял, о причине сообщил общий перехватчик.
   */
  const setActiveFilters = useCallback(
    async (filters: DashboardPresetFilters): Promise<boolean> => {
      if (!activePreset) return false;
      if (!activePreset.isShared) {
        updateDashboardPreset(userId, activePreset.id, { filters });
        return true;
      }
      if (!activePreset.isEditable) {
        setDashboardSharedFilters(userId, activePreset.id, filters);
        return true;
      }
      const sharedId = parseSharedDashboardPresetId(activePreset.id);
      if (sharedId === null) return false;
      const settings = buildSharedContent(sharedId, { filters });
      if (!settings) return false;
      const updated = await updateSharedFilters({ id: sharedId, settings }).catch(() => null);
      return updated !== null;
    },
    [activePreset, updateDashboardPreset, setDashboardSharedFilters, updateSharedFilters, buildSharedContent, userId],
  );

  /**
   * «Сбросить фильтры» на применённом дашборде. Получатель возвращается к фильтрам владельца — и
   * дальше правка владельца снова доходит до него, — у остальных дашборд получает фильтры по умолчанию.
   */
  const resetActiveFilters = useCallback(
    async (defaults: DashboardPresetFilters): Promise<boolean> => {
      if (activePreset?.isShared && !activePreset.isEditable) {
        setDashboardSharedFilters(userId, activePreset.id, null);
        return true;
      }
      return setActiveFilters(defaults);
    },
    [activePreset, setDashboardSharedFilters, setActiveFilters, userId],
  );

  /**
   * Забывает фильтры получателя у совместных дашбордов, которых в списке нет. Зовётся только
   * с полученным списком (`isPresetListKnown`): до него пропавшим выглядит любой совместный.
   */
  const pruneSharedFilters = useCallback(
    () => pruneDashboardSharedFilters(userId, new Set(sharedPresets.map((preset) => preset.id))),
    [pruneDashboardSharedFilters, sharedPresets, userId],
  );

  const apply = useCallback(
    (presetId: string) => applyDashboardPreset(userId, presetId),
    [applyDashboardPreset, userId],
  );

  const clearActive = useCallback(() => clearActiveDashboardPreset(userId), [clearActiveDashboardPreset, userId]);

  /**
   * Удаляет дашборд, у получателя совместного — убирает только из его списка. `onRemoved` — когда
   * дашборд удалён: у совместного по ответу сервера, отказ его не вызывает.
   */
  const remove = useCallback(
    (presetId: string, onRemoved?: () => void) => {
      // Совместный пропадает из списка только с ответом сервера, поэтому и применение снимается
      // по ответу: отказ оставляет дашборд применённым
      const isShared = deleteServerPreset(presetId, () => {
        if (activePresetId === presetId) clearActiveDashboardPreset(userId);
        onRemoved?.();
      });
      if (isShared) return;
      deleteDashboardPreset(userId, presetId);
      onRemoved?.();
    },
    [deleteDashboardPreset, deleteServerPreset, clearActiveDashboardPreset, userId, activePresetId],
  );

  const setPinned = useCallback(
    (presetId: string, pinned: boolean) => {
      if (!pinServerPreset(presetId, pinned)) setDashboardPresetPinned(userId, presetId, pinned);
    },
    [setDashboardPresetPinned, pinServerPreset, userId],
  );

  const findPersonalTransfer = useCallback(
    (presetId: string): SharedPresetTransfer<DashboardPresetContent> | undefined => {
      const preset = personalPresets.find((item) => item.id === presetId);
      return (
        preset && {
          name: preset.name,
          settings: { charts: preset.charts, filters: preset.filters },
          pinned: preset.pinned,
        }
      );
    },
    [personalPresets],
  );

  const findSharedTransfer = useCallback(
    (presetId: string): SharedPresetTransfer<DashboardPresetContent> | undefined => {
      const preset = sharedPresets.find((item) => item.id === presetId);
      return (
        preset && {
          name: preset.name,
          settings: { charts: preset.charts, filters: preset.filters },
          pinned: preset.pinned,
        }
      );
    },
    [sharedPresets],
  );

  const replaceWithShared = useCallback(
    (personalId: string, created: SharedPreset<DashboardPresetContent>, wasActive: boolean) => {
      // Применённость переходит на совместный до удаления личного — иначе удаление сняло бы её
      if (wasActive) applyDashboardPreset(userId, buildSharedDashboardPresetId(created.id));
      deleteDashboardPreset(userId, personalId);
    },
    [applyDashboardPreset, deleteDashboardPreset, userId],
  );

  const replaceWithPersonal = useCallback(
    (_sharedPresetId: string, preset: SharedPresetTransfer<DashboardPresetContent>, wasActive: boolean) => {
      // Личный — свой состав, а не ссылка на состав из ответа сервера
      const { charts, filters } = preset.settings;
      createDashboardPreset(
        userId,
        preset.name,
        structuredClone(charts),
        filters ? structuredClone(filters) : undefined,
        {
          pinned: preset.pinned,
          apply: wasActive,
        },
      );
    },
    [createDashboardPreset, userId],
  );

  /**
   * Поделиться дашбордом: личный становится совместным с теми же названием, составом, фильтрами
   * и закреплением, у совместного меняются получатели, без получателей он возвращается в личные.
   */
  const { share, isSharing } = useSharePreset<DashboardPresetContent>({
    tableName: DASHBOARD_PRESETS_TABLE_NAME,
    activePresetId,
    parseSharedId: parseSharedDashboardPresetId,
    findPersonal: findPersonalTransfer,
    findShared: findSharedTransfer,
    replaceWithShared,
    replaceWithPersonal,
  });

  /**
   * Заводит личную копию дашборда под переданным названием и применяет её: сам дашборд не меняется.
   * Копия получает фильтры, которые пользователь видит в панели.
   * @returns Идентификатор копии.
   */
  const saveCopy = useCallback(
    (presetId: string, name: string): string | null => {
      const preset = presets.find((item) => item.id === presetId);
      if (!preset) return null;
      const filters = resolveFilters(preset);
      // Копия — свой состав, а не ссылка на состав из ответа сервера
      return createDashboardPreset(
        userId,
        name,
        structuredClone(preset.charts),
        filters ? structuredClone(filters) : undefined,
      );
    },
    [createDashboardPreset, presets, resolveFilters, userId],
  );

  const chartTables = dashboardPresets.chartTables;

  const setChartTableSettings = useCallback(
    (chartId: string, settings: Partial<CustomChartTableSettings>) =>
      setDashboardChartTableSettings(userId, chartId, settings),
    [setDashboardChartTableSettings, userId],
  );

  return {
    presets,
    isPresetListKnown,
    /** Совместные дашборды ещё загружаются; после отказа запроса — уже нет, хотя список и не получен */
    isPresetListLoading: sharedPresetsQuery.isLoading,
    activePresetId,
    activePreset,
    /** Фильтры панели применённого дашборда; `null` — своих фильтров нет, действуют фильтры по умолчанию */
    activeFilters,
    /** Личный вид таблиц диаграмм по идентификатору диаграммы */
    chartTables,
    /** Запись состава совместного дашборда в работе */
    isUpdating: isUpdatingSharedPreset,
    isSharing,
    createPreset,
    updatePreset,
    setActiveFilters,
    resetActiveFilters,
    pruneSharedFilters,
    apply,
    clearActive,
    remove,
    setPinned,
    share,
    saveCopy,
    setChartTableSettings,
  };
};
